'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { client, CLIENT_COUNT, CLIENT_DETAILS, CLIENT_LIST, type ClientNr } from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AvatarStack, Field, fieldClasses, FieldLabel, HeaderStat, Tag } from './app-screen'
import styles from './client-dossier-visual.module.css'
import { useScenario } from './use-scenario'

type Client = {
  nr: string
  name: string
  tag: string
  indent?: boolean
  avatars: string[]
}

// The client added through the KvK flow. It is client 10010 in the canonical base; here it is still
// being created, so it has no number yet — and it cannot already be in the list it is added to.
const ADDED_NR: ClientNr = '10010'
const ADDED_CLIENT = client(ADDED_NR)

// The visible list is the shared client window, minus that client.
const LIST = CLIENT_LIST.filter((row) => row.nr !== ADDED_NR)

const CLIENTS: Client[] = LIST.map(({ nr, team }) => {
  const { name, form, parent } = client(nr)
  return { nr, name, tag: form, indent: Boolean(parent), avatars: [...team] }
})

// It lands on TOP of the list and pushes everything below it down — the bottom row simply runs past
// the card edge, the way a real, longer list would. With the client number it was given: it used to
// land on a dash, which is what an unsaved form shows, not what a client the register just filled
// in has. The dash was also what the resting frame showed, so the picture this card rests on was a
// row with a hole where the identifier goes.
const NEW_CLIENT: Client = {
  nr: ADDED_NR,
  name: ADDED_CLIENT.name,
  tag: 'NIEUW',
  avatars: [ADDED_CLIENT.manager, 'NK'],
}

// KvK details for the client being added — the same record `ClientRecordVisual` later shows in
// full, so the number, street and city stay in step across both visuals.
const ADDED_DETAILS = CLIENT_DETAILS[ADDED_NR]
const ADDED_CITY = ADDED_DETAILS.city

const BASE_COUNT = CLIENT_COUNT

/**
 * How long each beat stays on screen, and in which order.
 *
 * The order matters more than the numbers here, and it was wrong. The loop rested on the list with
 * the client already in it and cleared that row on its first beat, so a visitor arriving at the
 * page watched a client appear, be taken away, and be added again — the card undoing itself before
 * it had done anything. A story about adding something has to rest on the state before the add and
 * arrive at the state after it, which is what it does now: the plain list, the dialog, and the row
 * landing on the last beat with the counter behind it. The reset is the wrap, at the far end of the
 * beat the whole card was built to reach.
 *
 * Fourteen beats at a flat 950ms was the schedule before that. Two of them rendered what the beat
 * before them rendered, and three more filled one register field each — KvK number, then legal
 * form, then address — which is a form being typed into at a machine's pace, where the claim over
 * this card is the opposite: you pick the company and the register fills the form. They land
 * together, on the beat the suggestion is chosen.
 */
const BEAT_HOLDS = [1400, 800, 900, 900, 1200, 1400, 1000, 1800] as const

const REST_BEAT = 0 // SSR / reduced-motion snapshot — the client list, before any of this
const PRESSED_AT = 1 // the button takes its press
const MODAL_FROM = 2 // the dialog opens, empty
const TYPING_AT = 3 // enough letters to narrow the register
const DROPDOWN_AT = 4 // what the register turns up
const FILLED_AT = 5 // one is chosen, and every field it fills arrives at once
const ADDED_AT = 6 // Toevoegen → Toegevoegd
const ROW_AT = 7 // the dialog closes and the row is there — one event, and the card's whole point

function naamValue(beat: number): string {
  if (beat >= FILLED_AT) return ADDED_CLIENT.name
  if (beat >= TYPING_AT) return ADDED_CLIENT.short
  return ''
}

/**
 * Modules card 3 — clients list behind a "Klant toevoegen" modal: the KvK lookup is typed,
 * chosen from an autocomplete suggestion, the KvK fields flash-fill in sequence, and the new
 * client lands at the top of the list with the header counter ticking up. Driven by
 * `useScenario`'s 14-beat loop; `beat === null` (SSR, no-JS, reduced motion) renders the resolved
 * rest state: modal closed, new client added.
 */
export function ClientDossierVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = beat ?? REST_BEAT

  const buttonPressed = effBeat === PRESSED_AT
  const modalOpen = effBeat >= MODAL_FROM && effBeat < ROW_AT
  const dropdownOpen = effBeat === DROPDOWN_AT
  const showCaret = effBeat === TYPING_AT || effBeat === DROPDOWN_AT
  const naam = naamValue(effBeat)
  // Three fields off one choice, in one stroke. That is the whole of "zonder overtypen".
  const filled = effBeat >= FILLED_AT
  const kvkNummer = filled ? ADDED_DETAILS.kvk : undefined
  const rechtsvorm = filled ? 'Besloten vennootschap' : undefined
  const adres = filled ? `${ADDED_DETAILS.addresses[0].street} · ${ADDED_CITY}` : undefined
  const kvkPillVisible = filled
  const added = effBeat >= ADDED_AT

  const rowAdded = effBeat >= ROW_AT
  const count = rowAdded ? BASE_COUNT + 1 : BASE_COUNT

  return (
    <AppUiVisual label={t.screen.clientDossier.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="client-dossier" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.clientDossier.title}
            detail={t.screen.clientDossier.allClients}
            /* Dropped at phone width. The button on the right of this header is what the card is
               about and it is now the size of one, which at 284px leaves the title truncating to
               "Klanten / All…" — and "Alle klanten" is the half of that title a client list can
               afford to lose. */
            detailClassName={styles.hideNarrow}
            right={
              <div className="flex items-center gap-1.5">
                <HeaderStat value={count} label={t.word.clientsLower} />
                <span className={clsx(styles.addButton, buttonPressed && styles.addButtonPressed)}>
                  {t.screen.clientDossier.addClientPlus}
                </span>
              </div>
            }
          />

          <div className="flex min-h-0 flex-1 flex-col justify-start px-3 py-1">
            {/* The added client is always rendered and opens from nothing, rather than being spliced
                into the list and animated with keyframes. Keyframes run whenever the element first
                appears — including the moment the card is activated, which made a row nobody had
                added yet roll away before the story started — and they are switched off under
                reduced motion, which left the row standing there instead. A height that transitions
                is closed at rest, closed on activation, and closed for SSR, without any of that. */}
            <div className={clsx(styles.newRow, rowAdded && styles.newRowIn)}>
              <div className={clsx(styles.clientRow, rowAdded && styles.newRowMarked)}>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0 text-[8px]" style={{ color: 'var(--au-text2)' }}>
                    {NEW_CLIENT.nr}
                  </span>
                  <span className="truncate text-[10px] font-medium" style={{ color: 'var(--au-accent)' }}>
                    {NEW_CLIENT.name}
                  </span>
                  <Tag className="shrink-0">{NEW_CLIENT.tag}</Tag>
                </div>
                <AvatarStack initials={NEW_CLIENT.avatars} className="shrink-0" />
              </div>
            </div>

            {CLIENTS.map((client) => (
              <div key={client.nr} className={styles.clientRow}>
                <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: client.indent ? 12 : 0 }}>
                  {client.indent && (
                    <span className="shrink-0 text-[9px]" style={{ color: 'var(--au-text2)' }}>
                      └
                    </span>
                  )}
                  <span className="shrink-0 text-[8px]" style={{ color: 'var(--au-text2)' }}>
                    {client.nr}
                  </span>
                  <span className="truncate text-[10px] font-medium" style={{ color: 'var(--au-accent)' }}>
                    {client.name}
                  </span>
                  <Tag className="shrink-0">{client.tag}</Tag>
                </div>
                <AvatarStack initials={client.avatars} className="shrink-0" />
              </div>
            ))}
          </div>
        </div>

        <div className={clsx(styles.scrim, modalOpen && styles.scrimVisible)} aria-hidden="true">
          <div
            // 240px wide, the legal form read "Besloten vennootsc…" — a field truncating the one
            // value it exists to show, in the dialog that is the point of the card.
            className={clsx(styles.modal, 'w-[88%] max-w-[292px] rounded-[8px] border shadow-sm')}
            style={{ background: 'var(--au-surface)', borderColor: 'var(--au-border)' }}
          >
            <div
              className="flex items-center justify-between gap-2 border-b px-2.5 py-2"
              style={{ borderColor: 'var(--au-hairline)' }}
            >
              <span className="text-[10.5px] font-semibold whitespace-nowrap" style={{ color: 'var(--au-text)' }}>
                {t.screen.clientDossier.addClient}
              </span>
              <span
                className="rounded-[4px] px-1.5 py-0.5 text-[7px] font-bold whitespace-nowrap uppercase"
                style={{ background: 'var(--au-accent-soft)', color: 'var(--au-accent)', letterSpacing: '0.02em' }}
              >
                {t.screen.clientDossier.kvkIntegration}
              </span>
            </div>
            <div className="flex flex-col gap-1.5 px-2.5 py-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <FieldLabel>{t.word.name}</FieldLabel>
                <div
                  className={clsx(fieldClasses.box, naam && fieldClasses.filled)}
                  style={{ color: naam ? 'var(--au-text)' : 'var(--au-text2)' }}
                >
                  {naam || '—'}
                  {showCaret && (
                    <span aria-hidden style={{ color: 'var(--au-accent)' }}>
                      |
                    </span>
                  )}
                </div>
                <div className={clsx(styles.dropdown, dropdownOpen && styles.dropdownVisible)}>
                  <div className={styles.dropdownRowActive}>
                    <span className="truncate">
                      <b>{ADDED_CLIENT.name}</b> <span style={{ color: 'var(--au-text2)' }}>({ADDED_CITY})</span>
                    </span>
                    <span
                      className="shrink-0 text-[7.5px] font-bold whitespace-nowrap uppercase"
                      style={{ color: 'var(--au-accent)' }}
                    >
                      {t.screen.clientDossier.headOffice}
                    </span>
                  </div>
                  <div className={styles.dropdownRow}>
                    <span className="truncate">
                      Noorderpoort Beheer B.V. <span style={{ color: 'var(--au-text2)' }}>({ADDED_CITY})</span>
                    </span>
                    <span
                      className="shrink-0 text-[7.5px] font-bold whitespace-nowrap uppercase"
                      style={{ color: 'var(--au-text2)' }}
                    >
                      {t.screen.clientDossier.headOffice}
                    </span>
                  </div>
                </div>
              </div>
              {/* Side by side, or one above the other where side by side would truncate the legal
                  form — the one field on this form nobody could guess from the name above it. */}
              <div className={styles.fieldPair}>
                <Field label={t.screen.clientDossier.kvkNumber} value={kvkNummer} placeholder="—" className="flex-1" />
                <Field label={t.screen.clientDossier.legalForm} value={rechtsvorm} placeholder="—" className="flex-1" />
              </div>
              <Field label={t.word.address} value={adres} placeholder="—" />
              {/* Confirmation and action share one line straight under the last field, the way the
                  hours modal ends: the provenance note on the left, the button on the right. */}
              <div className="flex items-center justify-between gap-2 pt-0.5">
                <span className={clsx(styles.kvkPill, kvkPillVisible && styles.kvkPillVisible)}>
                  {t.screen.clientDossier.fromKvk}
                </span>
                <span className={clsx(styles.button, added && styles.buttonDone)}>
                  {added ? t.screen.clientDossier.added : t.screen.clientDossier.add}
                </span>
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
